import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

// "Living Room" (docs/design: Twinden brand directions, direction D).
// Warm stone neutrals carry the calm, one accent carries the life: Oxblood,
// the colour of the Twinden logo, with saffron for anything new.

const config: ThemeConfig = {
  initialColorMode: 'system',
  useSystemColorMode: true,
};

// People get soft, stable colours from their name (design: avatar pairs).
// Light: pastel ground, deep text. Dark: deep ground, pastel text.
const AVATAR_PAIRS = [
  { bg: ['#FFD6C7', '#4A2A20'], fg: ['#8A2E14', '#FFB8A3'] }, // peach
  { bg: ['#E4D9FA', '#2E2546'], fg: ['#4F3590', '#C9B6F5'] }, // lilac
  { bg: ['#CDEBD6', '#1F3A28'], fg: ['#1F6338', '#9ED9B2'] }, // mint
  { bg: ['#D2E3FA', '#1E2D45'], fg: ['#1F4C8A', '#A9C8F5'] }, // sky
  { bg: ['#F7E1B5', '#3D2E12'], fg: ['#7A5410', '#F2C878'] }, // honey
  { bg: ['#E8DCD0', '#33291F'], fg: ['#5A4634', '#D9C2AA'] }, // clay
] as const;

export function avatarPairIndex(name: string | undefined): number {
  let hash = 0;
  for (const char of name ?? '') hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return Math.abs(hash) % AVATAR_PAIRS.length;
}

const avatarTokens = Object.fromEntries(
  AVATAR_PAIRS.flatMap((pair, i) => [
    [`avatar.${i}.bg`, { default: pair.bg[0], _dark: pair.bg[1] }],
    [`avatar.${i}.fg`, { default: pair.fg[0], _dark: pair.fg[1] }],
  ])
);

const FONT_BODY = `'Hanken Grotesk Variable', 'Noto Sans Devanagari Variable', system-ui, -apple-system, 'Segoe UI', sans-serif`;
const FONT_DISPLAY = `'Bricolage Grotesque Variable', 'Hanken Grotesk Variable', 'Noto Sans Devanagari Variable', system-ui, sans-serif`;

export const theme = extendTheme({
  config,
  fonts: {
    heading: FONT_DISPLAY,
    body: FONT_BODY,
  },
  colors: {
    // Oxblood. 500 is the brand colour; 200 is its lifted form on dark.
    brand: {
      50: '#F2E9EA',
      100: '#E8D3D6',
      200: '#E58A94',
      300: '#C9606D',
      400: '#9A2A38',
      500: '#7A1F2B',
      600: '#6A1A25',
      700: '#5E1520',
      800: '#3A2124',
      900: '#2A1418',
    },
    // Chakra's gray, replaced with the design's warm stone so every existing
    // gray.* in the app follows the palette.
    gray: {
      50: '#FAF9F7',
      100: '#F0EEEA',
      200: '#E3DFD9',
      300: '#D6D0C6',
      400: '#ABA49A',
      500: '#8C857B',
      600: '#67625B',
      700: '#34302C',
      800: '#211E1B',
      900: '#171513',
    },
    // Anything new: unread, the current tab, fresh faces.
    saffron: {
      50: '#FDF4E2',
      100: '#FBE7BF',
      200: '#F7D68F',
      300: '#F4C566',
      400: '#F0B63F',
      500: '#F0B63F',
      600: '#B9811A',
      700: '#8F6312',
      800: '#5E410C',
      900: '#2F2106',
    },
  },
  semanticTokens: {
    colors: {
      // Grounds
      canvas: { default: '#FAF9F7', _dark: '#171513' },
      page: { default: '#EFEDE9', _dark: '#171513' },
      surface: { default: '#FFFFFF', _dark: '#211E1B' },
      'surface.subtle': { default: '#F0EEEA', _dark: '#2A2724' },
      'surface.raised': { default: '#FFFFFF', _dark: '#2A2724' },
      // Lines and text
      line: { default: '#E3DFD9', _dark: '#34302C' },
      ink: { default: '#1F1D1B', _dark: '#F3F0EB' },
      'ink.body': { default: '#3B3733', _dark: '#D6D0C6' },
      muted: { default: '#67625B', _dark: '#ABA49A' },
      // The accent: text/icons, solid fills, and its pale wash
      accent: { default: '#7A1F2B', _dark: '#E58A94' },
      'accent.solid': { default: '#7A1F2B', _dark: '#9A2A38' },
      'accent.tint': { default: '#F2E9EA', _dark: '#3A2124' },
      'accent.onSolid': { default: '#FFFFFF', _dark: '#FFFFFF' },
      // New and unread
      saffron: { default: '#F0B63F', _dark: '#F0B63F' },
      'saffron.text': { default: '#B9811A', _dark: '#F0B63F' },
      // The dark pill nav and composer stay dark in both modes.
      'nav.bg': { default: '#1F1D1B', _dark: '#2A2724' },
      'nav.fg': { default: '#E2DED7', _dark: '#D6D0C6' },
      // Destructive actions (design: confirmations).
      danger: { default: '#B42318', _dark: '#F08A8F' },
      'danger.solid': { default: '#B42318', _dark: '#C2362B' },
      'danger.tint': { default: '#FBE9E7', _dark: '#3D1A17' },
      // Chakra's own semantic tokens, on the stone palette
      'chakra-body-bg': { default: '#FAF9F7', _dark: '#171513' },
      'chakra-body-text': { default: '#1F1D1B', _dark: '#F3F0EB' },
      'chakra-border-color': { default: '#E3DFD9', _dark: '#34302C' },
      'chakra-placeholder-color': { default: '#67625B', _dark: '#ABA49A' },
      ...avatarTokens,
    },
  },
  shadows: {
    // Focus rings in the accent, not Chakra's default blue.
    outline: '0 0 0 3px rgba(122, 31, 43, 0.45)',
  },
  radii: {
    card: '22px',
    tile: '26px',
  },
  components: {
    Heading: {
      baseStyle: { letterSpacing: '-0.02em', fontWeight: 800 },
    },
    Button: {
      defaultProps: { colorScheme: 'brand' },
      baseStyle: { borderRadius: 'full', fontWeight: 700 },
      variants: {
        // Solid accent buttons use the mode-aware fill, not brand.200 on dark.
        solid: (props: { colorScheme: string }) =>
          props.colorScheme === 'brand'
            ? {
                bg: 'accent.solid',
                color: 'accent.onSolid',
                _hover: { bg: 'brand.600', _disabled: { bg: 'accent.solid' } },
                _active: { bg: 'brand.700' },
                _dark: { _hover: { bg: 'brand.400' } },
              }
            : {},
      },
    },
    IconButton: {
      baseStyle: { borderRadius: 'full' },
    },
    Input: {
      defaultProps: { focusBorderColor: 'accent' },
      variants: {
        outline: { field: { borderRadius: 'xl', bg: 'surface' } },
      },
    },
    Textarea: {
      defaultProps: { focusBorderColor: 'accent' },
    },
    Avatar: {
      baseStyle: (props: { name?: string }) => {
        const i = avatarPairIndex(props.name);
        return {
          container: {
            bg: `avatar.${i}.bg`,
            color: `avatar.${i}.fg`,
            fontWeight: 700,
            borderColor: 'canvas',
          },
        };
      },
    },
    Checkbox: {
      defaultProps: { colorScheme: 'brand' },
    },
    Radio: {
      defaultProps: { colorScheme: 'brand' },
    },
    Switch: {
      defaultProps: { colorScheme: 'brand' },
    },
    Badge: {
      baseStyle: {
        borderRadius: 'full',
        textTransform: 'none',
        fontWeight: 700,
      },
    },
    Menu: {
      baseStyle: {
        list: {
          bg: 'surface.raised',
          borderColor: 'line',
          borderRadius: 'xl',
          boxShadow: 'lg',
        },
        item: {
          bg: 'transparent',
          _hover: { bg: 'surface.subtle' },
          _focus: { bg: 'surface.subtle' },
        },
      },
    },
    Modal: {
      baseStyle: {
        dialog: { bg: 'surface', borderRadius: '2xl' },
      },
    },
  },
  styles: {
    global: {
      // Pages outside the app shell (login, join, admin) still scroll normally;
      // #root fills the viewport so the shell can size itself against it.
      'html, body, #root': {
        height: '100%',
      },
      body: {
        bg: 'canvas',
        color: 'ink',
        fontFeatureSettings: '"kern"',
      },
      // Inside the app on touch screens, only text people might copy is
      // selectable: message text (data-selectable, D140) and where they type.
      // Menus, sheets and buttons aren't: selection was starting under the
      // finger as the action sheet slid up (2026-10-22). Sheets render in
      // portals outside the shell, so the rule is on <html> while the shell
      // is mounted (Layout).
      '@media (hover: none) and (pointer: coarse)': {
        'html.in-app': {
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        },
        'html.in-app input, html.in-app textarea, html.in-app [contenteditable="true"], html.in-app [data-selectable="true"]':
          {
            userSelect: 'text',
            WebkitUserSelect: 'text',
            WebkitTouchCallout: 'default',
          },
      },
    },
  },
});
